import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Archive, Download, ExternalLink, Save, FolderCog } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function HRArchives() {
  const { user: currentUser } = useAuth();
  const isCEO = currentUser?.role === "CEO";
  const [items, setItems] = useState([]);
  const [driveUrl, setDriveUrl] = useState("");
  const [savingConfig, setSavingConfig] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = () => api.get("/hr-archives").then((r) => {
    setItems(r.data?.items || []);
    setDriveUrl(r.data?.config?.hr_archives_drive_folder_url || "");
  }).catch((e) => toast.error(e?.response?.data?.detail || "Load failed"));
  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put("/hr-archives/config", { hr_archives_drive_folder_url: driveUrl.trim() });
      toast.success(driveUrl.trim() ? "Drive destination saved" : "Reset to default (« 000-Archives RH »)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingConfig(false); }
  };

  const download = async (it) => {
    setDownloadingId(it.archive_id);
    try {
      const r = await api.get(`/hr-archives/${it.archive_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = it.filename || "archive.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const detail = e?.response?.data instanceof Blob ? "Download failed" : (e?.response?.data?.detail || "Download failed");
      toast.error(detail);
    } finally { setDownloadingId(null); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "—";
  const datasetsTotal = (d) => Object.values(d || {}).reduce((a, b) => a + (b || 0), 0);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1200px]">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2">Admin</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight flex items-center gap-3">
          <Archive size={28} className="text-zinc-500" /> HR Archives
        </h1>
        <p className="text-zinc-600 mt-2 max-w-xl text-sm">
          Every permanently deleted user leaves a full data export archived on Google Drive (ZIP: profile, CRA, missions, expenses, invoices, audit trail) — open it in Drive or download it here.
        </p>
      </div>

      {/* Drive destination config (CEO only) */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-6" data-testid="hr-archives-config">
        <div className="flex items-center gap-2 mb-3">
          <FolderCog size={16} className="text-zinc-500" />
          <h2 className="text-sm font-semibold">Drive destination for future archives</h2>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <Label className="text-xs text-zinc-600">Drive folder URL (leave empty = « 000-Archives RH » under the default client Drive parent)</Label>
            <Input
              value={driveUrl}
              onChange={(e) => setDriveUrl(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/…"
              disabled={!isCEO}
              data-testid="hr-archives-drive-url-input"
            />
          </div>
          <button
            onClick={saveConfig}
            disabled={!isCEO || savingConfig}
            data-testid="hr-archives-config-save"
            title={isCEO ? "Save" : "CEO only"}
            className="inline-flex items-center gap-2 px-4 h-10 bg-brand-navy text-white text-sm font-semibold rounded-md hover:bg-brand-navy-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Save size={14} /> {savingConfig ? "Saving…" : "Save"}
          </button>
        </div>
        {!isCEO && <p className="text-[11px] text-zinc-500 mt-2">Only the CEO can change the Drive destination.</p>}
      </div>

      {/* Archives list */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto md:overflow-hidden">
        <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200 font-mono">
          <div className="col-span-3">User</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Deleted on</div>
          <div className="col-span-2">By · Records</div>
          <div className="col-span-2 text-right">Archive</div>
        </div>
        {items.length === 0 && (
          <div className="px-5 py-16 text-sm text-zinc-500 text-center" data-testid="hr-archives-empty">
            No archive yet — archives appear when a user is permanently deleted.
          </div>
        )}
        {items.map((it) => (
          <div key={it.archive_id} className="block md:grid md:grid-cols-12 md:items-center px-4 md:px-5 py-3 md:py-4 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50" data-testid={`hr-archive-row-${it.archive_id}`}>
            <div className="md:col-span-3 font-medium text-sm truncate">{it.user_name || "—"}</div>
            <div className="md:col-span-3 text-sm text-zinc-600 font-mono truncate">{it.user_email || "—"}</div>
            <div className="md:col-span-2 text-sm text-zinc-600">{fmtDate(it.created_at)}</div>
            <div className="md:col-span-2 text-xs text-zinc-500">
              <div className="truncate">{it.deleted_by_name || "—"}</div>
              <div className="font-mono">{datasetsTotal(it.datasets)} record(s)</div>
            </div>
            <div className="md:col-span-2 flex md:justify-end items-center gap-2 mt-2 md:mt-0">
              {it.drive_web_view_link ? (
                <a
                  href={it.drive_web_view_link}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`hr-archive-drive-${it.archive_id}`}
                  title="Open in Drive"
                  className="inline-flex items-center gap-1 text-[11px] px-2 h-7 rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                >
                  <ExternalLink size={11} /> Drive
                </a>
              ) : (
                <span className="text-[10px] px-2 h-7 inline-flex items-center rounded border border-amber-200 bg-amber-50 text-amber-700" title={it.drive_warning || "No Drive copy"}>
                  No Drive
                </span>
              )}
              <button
                onClick={() => download(it)}
                disabled={!it.downloadable || downloadingId === it.archive_id}
                data-testid={`hr-archive-download-${it.archive_id}`}
                title={it.downloadable ? "Download ZIP (from Drive)" : "No copy available"}
                className="inline-flex items-center gap-1 text-[11px] px-2 h-7 rounded border border-zinc-200 text-zinc-700 hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Download size={11} className={downloadingId === it.archive_id ? "animate-pulse" : ""} /> ZIP
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
